import { z } from 'zod';
import { FilterCondition, OperatorSign } from '../types/enums/filter_condition';
import { DateFilter } from '../types/enums/date_filter';

// Segmentation Filter Schema
const segmentationFilterOperationSchema = z.object({
	SelectorUid: z.string(),
	Condition: z.nativeEnum(FilterCondition),
	Values: z.array(z.string()).default([]),
	DateFilter: z.nativeEnum(DateFilter).optional(),
});

// JSON Filter Schema
const jsonFilterOperationSchema = z.object({
	SelectorUid: z.string(),
	QueryValues: z
		.array(
			z.object({
				UiName: z.string().nonempty('UiName is required'),
				Type: z.enum([
					'string',
					'number',
					'boolean',
					'operator',
					'daysSince',
					'hoursSince',
					'String',
					'Number',
					'Boolean',
					'Operator',
					'DaysSince',
					'HoursSince',
					'dayssince',
					'hourssince',
					'eventName',
					'EventName',
					'Eventname',
				]),
				QueryName: z.string().nonempty('QueryName is required'),
				DefaultValue: z.union([z.string(), z.number(), z.boolean(), z.nativeEnum(OperatorSign)], {
					errorMap: () => ({ message: 'DefaultValue is required' }),
				}),
				Value: z
					.union([z.string(), z.number(), z.boolean(), z.nativeEnum(OperatorSign)])
					.nullable()
					.optional(), // Optional for incomplete filters
			}),
		)
		.optional(), // Optional for incomplete filters
	Query: z.string().nonempty('Query is required'), // Add Query field
});

// Combined Filter Schema
const filterOperationSchema = z.union([segmentationFilterOperationSchema, jsonFilterOperationSchema]);

// Main Segmentation Schema
const segmentationSchema = z
	.object({
		name: z.string().min(3, 'Name must be at least 3 characters'),
		description: z.string().min(3, 'Description must be at least 3 characters'),
		filterOperations: z.array(filterOperationSchema).min(1, 'At least one filter operation is required').nonempty(),
	})
	.superRefine((data, ctx) => {
		const { filterOperations } = data;

		filterOperations.forEach((filterOperation) => {
			// Validation for Condition (NoOp or Placeholder)
			if ('Condition' in filterOperation && filterOperation.Condition === FilterCondition.NoOp) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Filter condition cannot be NoOp',
					path: ['Condition'], // Specify the path for better error reporting
				});
			}
			// Validation for Segmentation Filters
			if ('Values' in filterOperation && Array.isArray(filterOperation.Values)) {
				if (filterOperation.Values.length === 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Values cannot be empty',
						path: ['Values'], // Specify the path for better error reporting
					});
				} else if (!filterOperation.Values.every((value) => value.trim() !== '')) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Values cannot contain empty strings',
						path: ['Values'],
					});
				}
			}
			// Additional validation for Segmentation Filters
			if ('DateFilter' in filterOperation && filterOperation.DateFilter !== undefined && filterOperation.DateFilter > 0) {
				const { Values } = filterOperation;

				if (Values) {
					Values.forEach((value) => {
						if (!Number.isInteger(Number(value))) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								message: 'Value must be an integer for date filter',
							});
						}
					});
				}
			}

			// Additional validation for JSON Filters
			if ('QueryValues' in filterOperation && filterOperation.QueryValues) {
				filterOperation.QueryValues.forEach((queryValue) => {
					const normalizedType = queryValue.Type.toLowerCase();
					if (normalizedType === 'number' && typeof queryValue.Value !== 'number') {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `Value for ${queryValue.UiName} must be a number`,
						});
					}
					if (normalizedType === 'dayssince' && (typeof queryValue.Value !== 'number' || queryValue.Value < 0)) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `Value for ${queryValue.UiName} must be a non-negative number`,
						});
					}
					if (normalizedType === 'hourssince' && (typeof queryValue.Value !== 'number' || queryValue.Value < 0)) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `Value for ${queryValue.UiName} must be a non-negative number`,
						});
					}
					if (normalizedType === 'string' && typeof queryValue.Value !== 'string') {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `Value for ${queryValue.UiName} must be a string`,
						});
					}
					if (normalizedType === 'boolean' && typeof queryValue.Value !== 'boolean') {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `Value for ${queryValue.UiName} must be a boolean`,
						});
					}

					if (normalizedType === 'operator' && (!queryValue.Value || queryValue.Value === '')) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `Please select an operator for ${queryValue.UiName}`,
						});
					}
					if (normalizedType === 'eventname' && (!queryValue.Value || queryValue.Value === '')) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `Please select an event name for ${queryValue.UiName}`,
						});
					}

				});
			}
		});
	});

export default segmentationSchema;
